import React, { useContext } from "react";
import { Link } from "gatsby";
import { twMerge } from "tailwind-merge";
import { AppContext } from "@context/AppContext";
import useDesktopNavigationData from "@staticQueries/desktopNavigation";
import { useScrollPosition } from "@utils/useScrollPosition";
import { MobileMenuButton } from ".";
import NavLink, { KontentNavigationLink } from "./NavLink";
import { SecondaryButton } from "@ui";

interface HeaderDataTypes {
  logo: { value: { url: string }[] };
  main_links: { value: KontentNavigationLink[] };
  secondary_links: { value: KontentNavigationLink[] };
}

const Header = ({
  textColor,
  logoOnly,
}: {
  textColor?: string;
  logoOnly?: boolean;
}) => {
  const { isSidebarOpen } = useContext(AppContext);
  const data: HeaderDataTypes = useDesktopNavigationData();
  const scrolled = useScrollPosition() > 0;
  return (
    <header
      className={`fixed z-[100] w-full transition duration-200 ${
        scrolled && !isSidebarOpen && "bg-white shadow"
      }`}
    >
      <nav className="mx-auto flex h-[85px] max-w-screen-lg items-center justify-between px-5 lg:h-[110px] xl:px-0">
        <Link to="/">
          {/* Note that even though a logo can be specified in the Mobile Navigation
          item in Kontent, only the desktop selection will be displayed */}
          <img
            src={data.logo.value[0].url}
            alt="CheckSmart Logo"
            width={175}
            height={28}
            className={`transition duration-200 md:w-[248px] ${
              isSidebarOpen && "opacity-0"
            }`}
          />
        </Link>
        {!logoOnly && (
          <>
            <div className="flex items-center gap-4">
              <a
                href="https://secure.checksmart.com/login/"
                className="flex rounded bg-salmon px-3 py-2 text-base font-bold not-italic leading-4 tracking-[0.36px] text-redWood shadow-[2px_2px_4px_0px_rgba(93,80,84,0.80)] lg:hidden"
              >
                Sign In
              </a>
              <MobileMenuButton />
            </div>
            {/* Desktop Navigation Links */}
            <div className="hidden items-end gap-[6px] md:flex-col lg:flex">
              {/* Rendering Secondary Links */}
              <ul className="flex gap-6">
                {data.secondary_links.value.map((link, idx) => (
                  <li key={idx}>
                    <NavLink
                      internalPath={link.elements.internal_path.value}
                      externalUrl={link.elements.external_url.value}
                      new_window={link.elements.new_window.value[0]?.codename}
                      variant="secondary"
                      className={twMerge(textColor, scrolled && "text-primary")}
                    >
                      {link.elements.text.value}
                    </NavLink>
                  </li>
                ))}
              </ul>
              {/* Rendering Main Links */}
              <ul className="flex gap-8">
                {data.main_links.value.map((link, idx) => (
                  <li key={idx}>
                    <NavLink
                      internalPath={link.elements.internal_path.value}
                      externalUrl={link.elements.external_url.value}
                      new_window={link.elements.new_window.value[0]?.codename}
                      variant="primary"
                      className={twMerge(textColor, scrolled && "text-primary")}
                    >
                      {link.elements.text.value}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
